import type { FirebaseRepository } from '../models/FirebaseRepository'

/**
 * Generates multiple unique group names, avoiding existing names and prioritizing unused adjectives and animals
 * @param count The number of group names to generate
 * @param existingNames Array of existing group names to avoid
 * @returns {string[]} An array of unique group names
 */
export function generateUniqueGroupNames(
  repository: FirebaseRepository,
  count: number,
  existingNames: string[] = []
): string[] {
  const values = repository.featureFlags.flags.automaticRoomNameValues

  // if flag is not set, use the original logic (Group 1, Group 2, etc.)
  if (!values) {
    return generateFallbackGroupName(count, existingNames)
  }

  const { adjectives, nouns } = values

  const names: string[] = []
  for (let i = 1; i <= count; i++) {
    let newName = createRandomRoomStateName(adjectives, nouns, existingNames)
    if (newName === '') {
      newName = generateFallbackGroupName(1, existingNames)[0]
    }
    names.push(newName)
    existingNames.push(newName)
  }
  return names
}

export function generateFallbackGroupName(
  count: number,
  existingNames: string[] = []
): string[] {
  const names: string[] = []
  let skipCount = 0
  for (let i = 1; i <= count + skipCount; i++) {
    const num = existingNames.length + i
    const _name = `Group ${num.toString().padStart(3, '0')}`
    // if _name already exists, skip it
    if (existingNames.includes(_name)) {
      skipCount++
      continue
    }
    names.push(_name)
  }
  return names
}

function createRandomRoomStateName(
  adjectives: string[],
  nounsMap: Record<string, string>,
  existingNames: string[]
): string {
  const adFreq: Record<string, number> = {}
  const animalFreq: Record<string, number> = {}
  const existingSet = new Set(existingNames)

  // Count frequencies.
  for (const name of existingNames) {
    const parts = name.split(/\s+/)
    if (parts.length < 3) continue
    adFreq[parts[1]] = (adFreq[parts[1]] || 0) + 1
    animalFreq[parts[2]] = (animalFreq[parts[2]] || 0) + 1
  }

  // Returns keys with minimum frequency.
  const minCandidates = (
    items: string[],
    freq: Record<string, number>
  ): string[] => {
    let min = Number.MAX_SAFE_INTEGER
    let candidates: string[] = []
    for (const item of items) {
      const count = freq[item] || 0
      if (count < min) {
        min = count
        candidates = [item]
      } else if (count === min) {
        candidates.push(item)
      }
    }
    return candidates
  }

  const leastAds = minCandidates(adjectives, adFreq)
  const animalList = Object.keys(nounsMap)
  const leastAnimals = minCandidates(animalList, animalFreq)

  // Shuffle candidates.
  const shuffle = (arr: string[]) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
  }
  shuffle(leastAds)
  shuffle(leastAnimals)

  // Try least-used combinations.
  for (const ad of leastAds) {
    for (const animal of leastAnimals) {
      const name = `${nounsMap[animal]} ${ad} ${animal}`
      if (!existingSet.has(name)) return name
    }
  }

  // Fallback: sort by frequency.
  const getSorted = (items: string[], freq: Record<string, number>) =>
    items.sort((a, b) => (freq[a] || 0) - (freq[b] || 0))

  const sortedAds = getSorted(adjectives, adFreq)
  const sortedAnimals = getSorted(animalList, animalFreq)

  for (const ad of sortedAds) {
    for (const animal of sortedAnimals) {
      const name = `${nounsMap[animal]} ${ad} ${animal}`
      if (!existingSet.has(name)) return name
    }
  }
  return ''
}
